import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { api } from "../api/client";

interface Usuario {
  id: string;
  nombre: string;
  email: string;
  rol: string;
  // Si ya vio el tour guiado de bienvenida (ver TourGuiado.tsx). Puede venir
  // undefined en respuestas viejas guardadas en memoria; se trata como
  // "ya lo vio" en ese caso, para no mostrar el tour de la nada.
  tourCompletado?: boolean;
}

interface Tenant {
  id: string;
  nombre: string;
  subdominio: string;
  logoUrl?: string | null;
  colorPrimario?: string;
  modulosActivos?: string[];
  estado?: "ACTIVO" | "INACTIVO" | "SUSPENDIDO";
  // Permisos personalizados por rol (ver Tenant.permisosRoles en el backend):
  // { "RECEPCIONISTA": { "inventario": "VER", "facturacion": "OCULTO" }, ... }
  permisosRoles?: Record<string, Record<string, "VER" | "EDITAR" | "OCULTO">> | null;
}

type PermisoSeccion = "VER" | "EDITAR" | "OCULTO";

interface AuthContextValue {
  usuario: Usuario | null;
  tenant: Tenant | null;
  cargando: boolean;
  soloLectura: boolean;
  // true si la suscripción del negocio está vencida (dentro de los 15 días
  // de gracia, ver EstadoSuscripcion.VENCIDA) — se usa para la barra amarilla
  // de aviso en AppLayout. No se repite si el negocio ya llegó a SUSPENDIDO
  // (ahí ya se muestra el aviso de soloLectura en su lugar).
  suscripcionVencida: boolean;
  // Si el tour guiado de bienvenida se debe estar mostrando ahora mismo.
  mostrarTour: boolean;
  // Vuelve a mostrar el tour guiado manualmente (botón "Ver tour" del
  // Topbar) — no cambia si quedó "visto" en el servidor, solo lo despliega
  // de nuevo en esta sesión.
  reiniciarTour: () => void;
  // Marca (en el servidor) que el usuario ya vio el tour y lo oculta.
  completarTour: () => void;
  login: (email: string, password: string) => Promise<void>;
  registrarNegocio: (data: {
    nombreNegocio: string;
    subdominio: string;
    tipoNegocio: string;
    nombreAdmin: string;
    email: string;
    password: string;
  }) => Promise<void>;
  logout: () => void;
  // El rol ADMIN siempre tiene acceso total, sin importar lo que diga
  // permisosRoles (así un negocio nunca puede bloquearse a sí mismo por
  // accidente). Para el resto de los roles, una sección sin entrada en
  // permisosRoles se comporta como "EDITAR" (el comportamiento de siempre).
  permisoSeccion: (seccion: string) => PermisoSeccion;
  // Actualiza los datos del negocio en memoria sin tener que recargar la
  // página ni volver a llamar a /auth/me — se usa justo después de guardar
  // cambios en Configuración (logo, color, módulos activos, permisos...).
  actualizarTenant: (datos: Partial<Tenant>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [cargando, setCargando] = useState(true);
  const [suscripcionVencida, setSuscripcionVencida] = useState(false);
  const [mostrarTour, setMostrarTour] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("vetflow_token");
    if (!token) {
      setCargando(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUsuario(res.data.usuario);
        setTenant(res.data.tenant);
        setMostrarTour(res.data.usuario?.tourCompletado === false);
      })
      .catch(() => {
        localStorage.removeItem("vetflow_token");
      })
      .finally(() => setCargando(false));
  }, []);

  // Consulta la suscripción por separado (no viene en /auth/me) en cuanto
  // hay una sesión de negocio activa. Si el rol no tiene acceso a esa
  // sección (permisoSeccion OCULTO) o falla por cualquier otro motivo,
  // simplemente no se muestra la barra de aviso — no es información crítica
  // para poder usar el resto del panel.
  useEffect(() => {
    if (!usuario) {
      setSuscripcionVencida(false);
      return;
    }
    api
      .get("/suscripcion")
      .then((res) => {
        setSuscripcionVencida(res.data?.suscripcion?.estado === "VENCIDA");
      })
      .catch(() => {
        setSuscripcionVencida(false);
      });
  }, [usuario?.id]);

  function guardarSesion(token: string, usuarioData: Usuario, tenantData: Tenant) {
    localStorage.setItem("vetflow_token", token);
    setUsuario(usuarioData);
    setTenant(tenantData);
    setMostrarTour(usuarioData?.tourCompletado === false);
  }

  async function login(email: string, password: string) {
    const res = await api.post("/auth/login", { email, password });
    guardarSesion(res.data.token, res.data.usuario, res.data.tenant);
  }

  async function registrarNegocio(data: {
    nombreNegocio: string;
    subdominio: string;
    tipoNegocio: string;
    nombreAdmin: string;
    email: string;
    password: string;
  }) {
    const res = await api.post("/auth/registro-negocio", data);
    guardarSesion(res.data.token, res.data.usuario, res.data.tenant);
  }

  function logout() {
    localStorage.removeItem("vetflow_token");
    setUsuario(null);
    setTenant(null);
    window.location.href = "/login";
  }

  const soloLectura = tenant?.estado === "SUSPENDIDO";

  function permisoSeccion(seccion: string): PermisoSeccion {
    if (!usuario) return "OCULTO";
    if (usuario.rol === "ADMIN") return "EDITAR";
    const entrada = tenant?.permisosRoles?.[usuario.rol]?.[seccion];
    return entrada ?? "EDITAR";
  }

  function actualizarTenant(datos: Partial<Tenant>) {
    setTenant((prev) => (prev ? { ...prev, ...datos } : prev));
  }

  function reiniciarTour() {
    setMostrarTour(true);
  }

  function completarTour() {
    setMostrarTour(false);
    setUsuario((prev) => (prev ? { ...prev, tourCompletado: true } : prev));
    // Sin await a propósito: no debe bloquear la interfaz, y si falla (sin
    // conexión, etc.) el tour de todos modos ya se ocultó en esta sesión —
    // simplemente podría volver a aparecer la próxima vez que inicie sesión.
    api.patch("/auth/tour-completado", { completado: true }).catch(() => {});
  }

  return (
    <AuthContext.Provider
      value={{
        usuario,
        tenant,
        cargando,
        soloLectura,
        suscripcionVencida,
        mostrarTour,
        reiniciarTour,
        completarTour,
        login,
        registrarNegocio,
        logout,
        permisoSeccion,
        actualizarTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
